import { TemplateCategory, TemplateGroups } from "components/EditorHome/Constants";
import { RulePrefix } from "constants/TemplateEditor";
import {
  IPage,
  ISection,
  ISectionRule,
  ITemplate,
  TemplateContent,
} from "interfaces/Templates";
import { isArray } from "lodash";

/**
 * @description - Get rule expression string to display without RulePrefix.DEFAULT
 * @param {string} ruleExpression - Original rule expression
 */
export const displayRuleExpression = (ruleExpression: string): string => {
  try {
    if (ruleExpression && typeof ruleExpression == "string") {
      return ruleExpression
        .replaceAll(RulePrefix.DEFAULT, "")
        .replaceAll(RulePrefix.NUMERIC, "");
    } else {
      return "";
    }
  } catch (error) {
    console.error("Rule expression display error =>", error);
    return ruleExpression;
  }
};

/**
 * @description - Get groupId value for a given group name
 * @param {any} groupName - group name
 * @param {Array<any>} groupData - all the existing group data related to templates
 * @returns {number} group Id
 */
export const getGroupIdByGroupName = (
  groupName: any,
  groupData: Array<any>
): number => {
  try {
    if (groupName != "" && groupData.length > 0) {
      return groupData.find((item) => item.groupName == groupName).groupId;
    } else return 0;
  } catch (error) {
    return 0;
  }
};

/**
 * @description - Retrieve section object by given section rule
 * @param {string}  name - Section name
 * @param {IPage[]} pages - Template pages
 */
export const getSectionByName = (
  name: string,
  pages: IPage[]
): ISection | undefined => {
  try {
    if (name) {
      const pageIndex = pages.findIndex((page) =>
        page.sections.some((section) => section.name === name)
      );
      const section = pages[pageIndex]?.sections.find(
        (section) => section.name === name
      );
      return section;
    } else {
      return undefined;
    }
  } catch (error) {
    return undefined;
  }
};

/**
 * @description - Retrieve default section rule object from the given section
 * @param {ISection} section - Section data
 */
export const getDefaultSectionRule = (
  section: ISection
): ISectionRule | undefined => {
  try {
    if (section && section.rules) {
      const defaultSectionRule = section.rules.find(
        (sectionRule: ISectionRule) => sectionRule.isDefault
      );
      return defaultSectionRule;
    } else {
      return undefined;
    }
  } catch (error) {
    return undefined;
  }
};

/**
 * @description - Retrieve section rule object by given section rule
 * @param {string}  rule - Section rule to check
 * @param {ISection} section - Section data
 */
export const getSectionRuleByRule = (
  rule: string,
  section: ISection
): ISectionRule | undefined => {
  try {
    if (rule) {
      const selectedSectionRule = section.rules.find(
        (sectionRule: ISectionRule) => sectionRule.rule == rule
      );
      return selectedSectionRule;
    } else {
      return getDefaultSectionRule(section);
    }
  } catch (error) {
    return getDefaultSectionRule(section);
  }
};

/**
 * @description - Check whether the given template content has repeating adapter(s) or not
 * @param {TemplateContent}  templateContent - Content of the template
 */
export const hasRepeatingAdapter = (
  templateContent: TemplateContent
): boolean => {
  try {
    if (templateContent && isArray(templateContent.adapter.repeatingAdapters)) {
      return templateContent.adapter.repeatingAdapters.length > 0;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

export const updateTemplateSectionRule = (
  sectionName: string,
  rule: string,
  newSectionRule: ISectionRule,
  template: ITemplate
): ITemplate => {
  try {
    if (template) {
      const updatedPages = template.templateContent?.pages?.map((page) => {
        return {
          ...page,
          sections: page.sections.map((pageSection: ISection) => {
            if (pageSection.name == sectionName) {
              return {
                ...pageSection,
                rules: pageSection.rules.map((sectionRule: ISectionRule) => {
                  if (sectionRule.rule == rule) {
                    return {
                      ...sectionRule,
                      ...newSectionRule,
                    };
                  } else {
                    return sectionRule;
                  }
                }),
              };
            } else {
              return pageSection;
            }
          }),
        };
      });
      const updatedTemplate: ITemplate = {
        ...template,
        templateContent: {
          ...template.templateContent,
          pages: updatedPages,
        },
      };
      return updatedTemplate;
    } else {
      return template;
    }
  } catch (error) {
    return template;
  }
};

/**
 * @description - Get adapter id list to get adapter token for the template
 * @returns - number array id
 */
export const getTemplateAdapterIds = ({
  template,
  isRepeating,
  all,
}: {
  template: ITemplate;
  isRepeating?: boolean;
  all?: boolean;
}): number[] => {
  try {
    if (template.templateContent.adapter) {
      const mainAdapterId = template.templateContent.adapter?.mainAdapterId;
      const repeatingAdapterIds =
        template.templateContent.adapter?.repeatingAdapters ?? [];
      if (isRepeating) {
        return [...repeatingAdapterIds];
      } else if (all) {
        return [mainAdapterId, ...repeatingAdapterIds];
      } else {
        return [mainAdapterId];
      }
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};


/**
 * @description - get message template add/remove option availability
 * @param {string}  groupName - Group name
 * @param {string} categoryName - Category name
 * @returns {boolean} message template availability
 */
export const getMessageTemplateAvailability = (
  groupName: string,
  categoryName: string
): boolean => {
  try {
    return (
      groupName != TemplateGroups.CREDITOR &&
      categoryName != TemplateCategory.EHF
    );
  }catch(error){
    return false;
  }
};

